// src/services/filesApi.ts
import apiReq, { backendHost } from 'core/api';
import { tokenStorage } from 'utils';
import type { ContextFile } from 'types';
import { ContextDocumentTargetType } from 'types';
import { uploadFileChunked, PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING } from 'utils/chunkedFileUpload';
import {
  registerProcessEventCallback,
  unregisterProcessEventCallback,
  type ProcessEventData,
} from 'events/webSocketHandlers';

export interface UploadOptions {
  onProgress?: (percent: number) => void;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024;

export const getContextFiles = async (chatId: string): Promise<ContextFile[]> => {
  const result = await apiReq<ContextFile[]>(`projects/context-documents?chat_id=${chatId}`, 'GET');
  return result ?? [];
};

/**
 * После загрузки в ответе приходит file_id, а в списке контекстных документов — id записи.
 * Без приведения чекбоксы не совпадают с selectedFileIds (счётчик «выбрано» растёт, галочек нет).
 */
export function resolveContextDocumentId(
  files: ContextFile[],
  uploadReturnedId: string,
  fileName: string
): string {
  if (files.some((f) => f.id === uploadReturnedId)) {
    return uploadReturnedId;
  }
  const sameName = files.filter((f) => f.name === fileName);
  if (sameName.length === 1) return sameName[0].id;
  if (sameName.length > 1) {
    return [...sameName].sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
    })[0].id;
  }
  return uploadReturnedId;
}

export const deleteContextFile = async (documentId: string): Promise<void> => {
  await apiReq(`projects/context-documents/delete?document_id=${documentId}`, 'DELETE');
};

export interface ContextDocumentBlob {
  blob: Blob;
  /** MIME из ответа (Content-Type), может быть пустым */
  mimeType: string;
  /** Имя файла из Content-Disposition, если сервер его прислал */
  fileName: string | null;
}

/** Парсит filename из заголовка Content-Disposition (filename*=UTF-8'' и filename="...") */
function parseContentDispositionFileName(header: string | null): string | null {
  if (!header) return null;
  const utf8 = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].replace(/^"|"$/g, '').trim());
    } catch {
      /* ignore */
    }
  }
  const ascii = header.match(/filename="?([^";]+)"?/i);
  return ascii?.[1]?.trim() ?? null;
}

/**
 * Качает контекстный документ как Blob для предпросмотра.
 * Отдельно от apiReq, т.к. apiReq всегда делает res.json(), а тут бинарь.
 */
export const fetchContextDocumentBlob = async (
  documentId: string
): Promise<ContextDocumentBlob> => {
  const token = tokenStorage.get();
  const res = await fetch(`${backendHost}/projects/context-documents/${documentId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Не удалось загрузить файл (${res.status})`);
  }
  const blob = await res.blob();
  return {
    blob,
    mimeType: res.headers.get('Content-Type') ?? blob.type ?? '',
    fileName: parseContentDispositionFileName(res.headers.get('Content-Disposition')),
  };
};

/**
 * Скачивает контекстный документ на устройство пользователя.
 * Имя берём из Content-Disposition (если сервер прислал), иначе из листинга.
 */
export const downloadContextDocument = async (
  documentId: string,
  fallbackName: string
): Promise<void> => {
  const { blob, fileName } = await fetchContextDocumentBlob(documentId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || fallbackName || 'document';
  a.click();
  URL.revokeObjectURL(url);
};

interface UploadResult {
  document?: ContextFile;
  error?: string;
}

export const uploadContextFile = async (
  projectId: string,
  target: { type: ContextDocumentTargetType; id: string },
  file: File,
  options: UploadOptions = {}
): Promise<UploadResult> => {
  const { onProgress } = options;

  if (file.size > MAX_FILE_SIZE) {
    return { error: `Файл слишком большой (максимум 2 ГБ)` };
  }

  const pendingProcesses: Record<string, { resolve: () => void; reject: (err: Error) => void }> =
    {};

  const handleProcessEvent = (_eventName: string, data: ProcessEventData) => {
    const pending = pendingProcesses[data.process_id];
    if (!pending) return;
    if (data.status === 'success' || data.status === 'uploaded') {
      pending.resolve();
      delete pendingProcesses[data.process_id];
    } else if (data.status === 'error') {
      pending.reject(new Error(data.message || 'Ошибка обработки'));
      delete pendingProcesses[data.process_id];
    }
  };

  registerProcessEventCallback(handleProcessEvent);

  try {
    const fileId = await uploadFileChunked(
      file,
      projectId,
      PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING,
      (pct) => onProgress?.(Math.min(pct, 90))
    );

    onProgress?.(92);

    const processData = await apiReq<{ process_id?: string; processId?: string }>(
      'process/context-document-parsing',
      'POST',
      { file_id: fileId, target_type: target.type, target_id: target.id }
    );
    const processId = processData?.process_id ?? processData?.processId;

    if (!processId) {
      throw new Error('Сервер не вернул process_id');
    }

    await new Promise<void>((resolve, reject) => {
      pendingProcesses[processId] = { resolve, reject };
    });

    onProgress?.(100);

    return { document: { id: fileId, name: file.name } as ContextFile };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Ошибка загрузки файла';
    return { error: message };
  } finally {
    unregisterProcessEventCallback();
  }
};
